import { useEffect, useRef, useState, type ReactNode } from "react";

function getCookieValue(name: string): string | null {
  const match = document.cookie.match(new RegExp("(?:^|;\\s*)" + name + "=([^;]*)"));
  return match ? match[1] : null;
}

function useSessionAdmin(): { isAdmin: boolean; inviteLabel: string | null; inviteCode: string | null } {
  const [state, setState] = useState<{ isAdmin: boolean; inviteLabel: string | null; inviteCode: string | null }>({
    isAdmin: false,
    inviteLabel: null,
    inviteCode: null,
  });
  const lastTokenRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const tokenRes = await fetch("/api/public/token-info", { credentials: "include" });
        if (cancelled) return;
        if (tokenRes.ok) {
          const tokenData = await tokenRes.json();
          const isAdmin = !!tokenData.isAdmin;
          const rawCode = tokenData.inviteCode || null;
          const rawLabel = tokenData.inviteLabel || null;
          const inviteCode = (rawCode === "ADMIN" || isAdmin) ? null : rawCode;
          const inviteLabel = (rawLabel && /admin/i.test(rawLabel)) || isAdmin ? null : rawLabel;
          setState({ isAdmin, inviteLabel, inviteCode });
        } else {
          setState(prev => ({ ...prev, isAdmin: false }));
        }
      } catch { }
    }

    check();

    const interval = setInterval(() => {
      const currentToken = getCookieValue("resonance_session");
      if (currentToken !== lastTokenRef.current) {
        lastTokenRef.current = currentToken;
        check();
      }
    }, 500);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  return state;
}

function formatWatermarkDate(): string {
  const now = new Date();
  return now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function WatermarkOverlay({ inviteLabel, inviteCode }: { inviteLabel: string | null; inviteCode: string | null }) {
  const parts: string[] = [];
  if (inviteLabel) parts.push(inviteLabel);
  if (inviteCode) parts.push(inviteCode);
  parts.push(formatWatermarkDate());
  const text = parts.join(" • ");

  return (
    <div
      data-testid="watermark-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        pointerEvents: "none",
        overflow: "hidden",
        opacity: 0.035,
      }}
    >
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern
            id="watermark-pattern"
            x="0"
            y="0"
            width="400"
            height="200"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(-35)"
          >
            <text
              x="10"
              y="100"
              fill="white"
              fontSize="14"
              fontFamily="monospace"
              fontWeight="bold"
            >
              {text}
            </text>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#watermark-pattern)" />
      </svg>
    </div>
  );
}

function useDevToolsDetection(isAdmin: boolean) {
  const [devToolsOpen, setDevToolsOpen] = useState(false);
  const consecutiveHitsRef = useRef(0);
  const REQUIRED_CONSECUTIVE_HITS = 3;

  useEffect(() => {
    if (isAdmin) {
      setDevToolsOpen(false);
      return;
    }

    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) {
      setDevToolsOpen(false);
      return;
    }

    let threshold = 160;

    const checkDevTools = () => {
      if (window.outerWidth === 0 || window.outerHeight === 0) {
        consecutiveHitsRef.current = 0;
        return;
      }
      const widthDiff = window.outerWidth - window.innerWidth > threshold;
      const heightDiff = window.outerHeight - window.innerHeight > threshold;
      if (widthDiff || heightDiff) {
        consecutiveHitsRef.current++;
        if (consecutiveHitsRef.current >= REQUIRED_CONSECUTIVE_HITS) {
          setDevToolsOpen(true);
        }
      } else {
        consecutiveHitsRef.current = 0;
        setDevToolsOpen(false);
      }
    };

    const checkDebugger = () => {
      const start = performance.now();
      (function () {})
        .constructor("debugger")();
      const duration = performance.now() - start;
      if (duration > 100) {
        consecutiveHitsRef.current = REQUIRED_CONSECUTIVE_HITS;
        setDevToolsOpen(true);
      }
    };

    const interval = setInterval(() => {
      checkDevTools();
      checkDebugger();
    }, 1000);

    window.addEventListener("resize", checkDevTools);

    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", checkDevTools);
    };
  }, [isAdmin]);

  return devToolsOpen;
}

export function ContentProtectionProvider({ children }: { children: ReactNode }) {
  const { isAdmin, inviteLabel, inviteCode } = useSessionAdmin();
  const devToolsOpen = useDevToolsDetection(isAdmin);
  const isAdminRef = useRef(isAdmin);
  isAdminRef.current = isAdmin;

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      if (isAdminRef.current) return;
      e.preventDefault();
      return false;
    };

    const handleCopy = (e: ClipboardEvent) => {
      if (isAdminRef.current) return;
      e.preventDefault();
      return false;
    };

    const handleCut = (e: ClipboardEvent) => {
      if (isAdminRef.current) return;
      e.preventDefault();
      return false;
    };

    const handlePaste = (e: ClipboardEvent) => {
      if (isAdminRef.current) return;
      return;
    };

    const handleDragStart = (e: DragEvent) => {
      if (isAdminRef.current) return;
      e.preventDefault();
      return false;
    };

    const handleDrop = (e: DragEvent) => {
      if (isAdminRef.current) return;
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isAdminRef.current) return;
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;

      if (e.key === "F12") {
        e.preventDefault();
        return false;
      }

      if (ctrlOrCmd && e.shiftKey && (e.key === "I" || e.key === "i")) {
        e.preventDefault();
        return false;
      }

      if (ctrlOrCmd && e.shiftKey && (e.key === "J" || e.key === "j")) {
        e.preventDefault();
        return false;
      }

      if (ctrlOrCmd && e.shiftKey && (e.key === "C" || e.key === "c")) {
        e.preventDefault();
        return false;
      }

      if (e.metaKey && e.altKey && (e.key === "I" || e.key === "i")) {
        e.preventDefault();
        return false;
      }

      if (e.metaKey && e.altKey && (e.key === "J" || e.key === "j")) {
        e.preventDefault();
        return false;
      }

      if (e.metaKey && e.altKey && (e.key === "C" || e.key === "c")) {
        e.preventDefault();
        return false;
      }

      if (e.metaKey && e.altKey && (e.key === "U" || e.key === "u")) {
        e.preventDefault();
        return false;
      }

      if (ctrlOrCmd && e.shiftKey && (e.key === "K" || e.key === "k")) {
        e.preventDefault();
        return false;
      }

      if (ctrlOrCmd && e.shiftKey && (e.key === "E" || e.key === "e")) {
        e.preventDefault();
        return false;
      }

      if (ctrlOrCmd && (e.key === "u" || e.key === "U")) {
        e.preventDefault();
        return false;
      }

      if (ctrlOrCmd && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        return false;
      }

      if (ctrlOrCmd && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
        return false;
      }

      if (ctrlOrCmd && (e.key === "a" || e.key === "A")) {
        e.preventDefault();
        return false;
      }
    };

    const handleSelectStart = (e: Event) => {
      if (isAdminRef.current) return;
      e.preventDefault();
      return false;
    };

    document.addEventListener("contextmenu", handleContextMenu, true);
    document.addEventListener("copy", handleCopy, true);
    document.addEventListener("cut", handleCut, true);
    document.addEventListener("paste", handlePaste, true);
    document.addEventListener("dragstart", handleDragStart, true);
    document.addEventListener("drop", handleDrop, true);
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("selectstart", handleSelectStart, true);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu, true);
      document.removeEventListener("copy", handleCopy, true);
      document.removeEventListener("cut", handleCut, true);
      document.removeEventListener("paste", handlePaste, true);
      document.removeEventListener("dragstart", handleDragStart, true);
      document.removeEventListener("drop", handleDrop, true);
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("selectstart", handleSelectStart, true);
    };
  }, []);

  const showDevToolsWarning = devToolsOpen && !isAdmin;

  return (
    <div
      data-testid="content-protection-wrapper"
      style={{
        filter: showDevToolsWarning ? "blur(30px)" : "none",
        transition: "filter 0.15s ease",
        pointerEvents: showDevToolsWarning ? "none" : "auto",
      }}
    >
      {children}
      {!isAdmin && <WatermarkOverlay inviteLabel={inviteLabel} inviteCode={inviteCode} />}
      {showDevToolsWarning && (
        <div
          data-testid="devtools-warning"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.95)",
            pointerEvents: "auto",
          }}
        >
          <p style={{ color: "#2DD4D8", fontFamily: "monospace", fontSize: "18px" }}>
            Content protected. Please close developer tools.
          </p>
        </div>
      )}
    </div>
  );
}